import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import { authFetch } from "./authFetch";

export interface ClientUserSession {
  id: string;
  clientId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  mustChangePassword: boolean;
}

export interface ClientInfo {
  id: string;
  companyName: string;
  nomCommercial: string | null;
  numeroClient: number | null;
  logoUrl: string | null;
  couleurPrimaire?: string | null;
  couleurSecondaire?: string | null;
  contactEmail: string;
  commercialId: string | null;
  technicienReferentId: string | null;
}

interface AuthState {
  user: ClientUserSession | null;
  client: ClientInfo | null;
  brand: { logoUrl: string | null; couleurPrimaire: string; couleurSecondaire: string };
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (token: string, user: ClientUserSession, client: ClientInfo) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  updateCompanyBrand: (brand: Partial<AuthState["brand"]>) => void;
}

const AuthContext = createContext<AuthState>({
  user: null,
  client: null,
  brand: { logoUrl: null, couleurPrimaire: "#4e63f0", couleurSecondaire: "#2facf3" },
  isLoading: true,
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
  refreshUser: async () => {},
  updateCompanyBrand: () => {},
});

export function ClientAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ClientUserSession | null>(null);
  const [client, setClient] = useState<ClientInfo | null>(null);
  const [brand, setBrand] = useState<AuthState["brand"]>({
    logoUrl: null, couleurPrimaire: "#4e63f0", couleurSecondaire: "#2facf3",
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchMe = async () => {
    const token = localStorage.getItem("ec_token");
    if (!token) { setIsLoading(false); return; }
    try {
      const res = await authFetch("/api/espace-client/me");
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        setClient(data.client);
        setBrand({
          logoUrl: data.client?.logoUrl || null,
          couleurPrimaire: data.client?.couleurPrimaire || "#4e63f0",
          couleurSecondaire: data.client?.couleurSecondaire || "#2facf3",
        });
      } else {
        localStorage.removeItem("ec_token");
        setUser(null);
        setClient(null);
      }
    } catch {
      setUser(null);
      setClient(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const publicAuthPaths = ["/activation", "/reinitialisation", "/mot-de-passe-oublie"];
    if (publicAuthPaths.includes(window.location.pathname)) {
      setIsLoading(false);
      return;
    }
    fetchMe();
  }, []);

  const login = (token: string, userData: ClientUserSession, clientData: ClientInfo) => {
    localStorage.setItem("ec_token", token);
    setUser(userData);
    setClient(clientData);
    setBrand({
      logoUrl: clientData.logoUrl || null,
      couleurPrimaire: clientData.couleurPrimaire || "#4e63f0",
      couleurSecondaire: clientData.couleurSecondaire || "#2facf3",
    });
  };

  const logout = () => {
    localStorage.removeItem("ec_token");
    setUser(null);
    setClient(null);
    setBrand({ logoUrl: null, couleurPrimaire: "#4e63f0", couleurSecondaire: "#2facf3" });
  };

  const refreshUser = async () => { await fetchMe(); };
  const updateCompanyBrand = useCallback((next: Partial<AuthState["brand"]>) => {
    setBrand(current => ({ ...current, ...next }));
    if (next.logoUrl !== undefined) {
      const logoUrl = next.logoUrl;
      setClient(current => current ? { ...current, logoUrl } : current);
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      client,
      brand,
      isLoading,
      isAuthenticated: !!user,
      login,
      logout,
      refreshUser,
      updateCompanyBrand,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useClientAuth() {
  return useContext(AuthContext);
}
